import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import ChatWidget from "@/components/ChatWidget";

import BusinessProfile from "@/pages/BusinessProfile";
import BusinessHandlePage from "@/pages/BusinessHandlePage";
import AddBusinessPage from "@/pages/AddBusinessPage";
import Messages from "@/pages/Messages";

import Landing from "@/pages/Landing";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Auth from "@/pages/Auth";
import BusinessSubscribe from "@/pages/BusinessSubscribe";
import BusinessDashboard from "@/pages/BusinessDashboard";
import BusinessBilling from "@/pages/BusinessBilling";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SystemDiagnostics from "@/pages/SystemDiagnostics";
import AIMonitoring from "@/pages/AIMonitoring";
import AuthCallback from "@/pages/AuthCallback";
import CustomerCompleteProfile from "@/pages/CustomerCompleteProfile";
import BusinessCompleteProfile from "@/pages/BusinessCompleteProfile";
import AdminCases from "@/pages/AdminCases";
import AboutPage from "@/pages/AboutPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AccountSecurity from "@/pages/AccountSecurity";
import AccountSettings from "@/pages/AccountSettings";
import PaymentSuccess from "@/pages/PaymentSuccess";
import ForgotPassword from "@/pages/ForgotPassword";
import Notifications from "@/pages/Notifications";
import { Navigate } from "react-router-dom";
import Help from "@/pages/Help";
import Cart from "@/pages/Cart";
import MobileVerification from "@/pages/MobileVerification";
import PayBalance from "@/pages/PayBalance";
import CookieConsent from "@/components/CookieConsent";
import CookiesPage from "@/pages/CookiesPage";
import NotFound from "@/pages/NotFound";
import RequireAuth from "@/components/RequireAuth";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, initPerfObserver } from "@/lib/analytics";

function AnalyticsGate() {
  const loc = useLocation();
  useEffect(() => { trackPageView(loc.pathname + loc.search); }, [loc]);
  useEffect(() => { initPerfObserver(); }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <Toaster position="top-right" richColors />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:id" element={<ServiceDetail />} />
                <Route path="/login" element={<Auth mode="login" />} />
                <Route path="/signup" element={<Auth mode="signup" />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/account/complete-profile" element={<CustomerCompleteProfile />} />
                <Route path="/business/complete-profile" element={<BusinessCompleteProfile />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
                {/* Legacy email-link reset — permanently retired. Send users back to the code flow. */}
                <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />
                <Route path="/help" element={<Help />} />
                <Route path="/cookies" element={<CookiesPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/account/security" element={<RequireAuth><AccountSecurity /></RequireAuth>} />
                <Route path="/account/settings" element={<RequireAuth><AccountSettings /></RequireAuth>} />
                <Route path="/admin/cases" element={<RequireAuth roles={["super_admin"]}><AdminCases /></RequireAuth>} />
                <Route path="/faq" element={<Help />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/business/subscribe" element={<RequireAuth roles={["business", "super_admin"]}><BusinessSubscribe /></RequireAuth>} />
                <Route path="/business/billing" element={<RequireAuth roles={["business", "super_admin"]}><BusinessBilling /></RequireAuth>} />
                <Route path="/business/mobile-verification" element={<RequireAuth roles={["business", "super_admin"]}><MobileVerification /></RequireAuth>} />
                <Route path="/pay-balance/:token" element={<RequireAuth><PayBalance /></RequireAuth>} />
                <Route path="/business/handle" element={<RequireAuth roles={["business", "super_admin"]}><BusinessHandlePage /></RequireAuth>} />
                <Route path="/business/:id" element={<BusinessProfile />} />
                <Route path="/business" element={<RequireAuth roles={["business", "super_admin"]}><BusinessDashboard /></RequireAuth>} />
                <Route path="/account" element={<RequireAuth><CustomerDashboard /></RequireAuth>} />
                <Route path="/account/add-business" element={<RequireAuth><AddBusinessPage /></RequireAuth>} />
                <Route path="/admin" element={<RequireAuth roles={["super_admin"]}><AdminDashboard /></RequireAuth>} />
                <Route path="/admin/diagnostics" element={<RequireAuth roles={["super_admin"]}><SystemDiagnostics /></RequireAuth>} />
                <Route path="/admin/ai-monitoring" element={<RequireAuth roles={["super_admin"]}><AIMonitoring /></RequireAuth>} />
                <Route path="/messages" element={<RequireAuth><Messages /></RequireAuth>} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <ChatWidget />
              <CookieConsent />
              <AnalyticsGate />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
